import React, { useState } from "react";
import { helpConcerns } from "../mock/extra";
import { Sparkles, Smile, Frown, Baby, Zap, Wrench, Droplet, ShieldAlert, ArrowRight, Check } from "lucide-react";

const iconFor = (key) => {
  const m = {
    missing: <Smile className="w-6 h-6" />,
    crooked: <Sparkles className="w-6 h-6" />,
    stained: <Droplet className="w-6 h-6" />,
    pain: <Zap className="w-6 h-6" />,
    gums: <ShieldAlert className="w-6 h-6" />,
    broken: <Wrench className="w-6 h-6" />,
    kids: <Baby className="w-6 h-6" />,
    makeover: <Frown className="w-6 h-6" />,
  };
  return m[key] || <Sparkles className="w-6 h-6" />;
};

export default function HelpMeWith() {
  const [active, setActive] = useState(0);
  const item = helpConcerns[active];

  return (
    <section id="help" className="py-24 md:py-32 bg-white relative overflow-hidden">
      <div className="absolute -top-40 right-0 w-[480px] h-[480px] rounded-full bg-[#14a68e]/8 blur-3xl pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="section-eyebrow">Personalised Care</div>
          <h2 className="mt-4 font-serif-display text-[42px] md:text-[62px] leading-[1.05] text-[#0e2b2b]">
            Help me <span className="hero-italic">with…</span>
          </h2>
          <p className="mt-4 text-[15px] md:text-[16px] text-[#0e2b2b]/70">
            Pick your concern and see the exact treatment options we recommend at Dr. Khurana’s Dental &amp; Aesthetic Clinic in Lucknow.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-8">
          {/* Concerns list */}
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3">
              {helpConcerns.map((c, i) => {
                const isActive = active === i;
                return (
                  <button
                    key={c.title}
                    onClick={() => setActive(i)}
                    className={`group text-left p-4 md:p-5 rounded-2xl border transition-all duration-300 relative overflow-hidden ${isActive ? "bg-[#0e2b2b] border-[#0e2b2b] text-white shadow-xl scale-[1.02]" : "bg-[#f6f7f4] border-[#0e2b2b]/10 hover:border-[#14a68e]/50 hover:-translate-y-0.5"}`}
                  >
                    <span className={`inline-flex w-10 h-10 rounded-xl items-center justify-center transition ${isActive ? "bg-[#14a68e] text-white" : "bg-white text-[#14a68e] group-hover:bg-[#14a68e] group-hover:text-white"}`}>
                      {iconFor(c.icon)}
                    </span>
                    <div className={`mt-3 text-[11px] tracking-[0.18em] uppercase font-semibold ${isActive ? "text-[#5eead4]" : "text-[#14a68e]"}`}>{c.tag}</div>
                    <div className={`mt-1 font-serif-display text-[17px] md:text-[18px] leading-tight ${isActive ? "text-white" : "text-[#0e2b2b]"}`}>{c.title}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detail panel */}
          <div className="lg:col-span-7">
            <div key={active} className="relative bg-[#0e2b2b] text-white rounded-[32px] p-8 md:p-10 overflow-hidden animate-[fadein_.5s_ease-out]">
              <div className="absolute -top-20 -right-20 w-[280px] h-[280px] rounded-full bg-[#14a68e]/30 blur-3xl" />
              <div className="relative">
                <div className="flex items-center gap-3">
                  <span className="w-12 h-12 rounded-2xl bg-[#14a68e] text-white flex items-center justify-center">{iconFor(item.icon)}</span>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-[#5eead4] font-semibold">Recommended Care</div>
                </div>
                <h3 className="mt-6 font-serif-display text-[32px] md:text-[42px] leading-[1.05]">{item.title}</h3>
                <p className="mt-3 text-[13px] tracking-[0.15em] uppercase text-white/60">Common Symptoms</p>
                <p className="mt-1 text-[15px] text-white/85">{item.problem}</p>

                <p className="mt-6 text-[15px] md:text-[16px] text-white/85 leading-relaxed max-w-[560px]">{item.lead}</p>

                <div className="mt-8">
                  <div className="text-[11px] tracking-[0.22em] uppercase text-[#5eead4] font-semibold">Treatments We Offer</div>
                  <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                    {item.solutions.map((s, i) => (
                      <li key={s} className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3 border border-white/10 backdrop-blur-sm transition hover:bg-white/10" style={{ animation: `slidein .5s ease-out ${i * 80}ms both` }}>
                        <span className="w-7 h-7 rounded-full bg-[#14a68e] text-white flex items-center justify-center shrink-0"><Check size={14} /></span>
                        <span className="text-[14px] font-medium">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-8">
                  <a href="#contact" className="inline-flex items-center gap-2 bg-white text-[#0e2b2b] rounded-full px-6 py-3 text-[13px] font-semibold uppercase tracking-wide hover:bg-[#5eead4] transition">
                    Book a Consultation <ArrowRight size={16} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadein { from { opacity: 0; transform: translateY(12px);} to { opacity: 1; transform: translateY(0);} }
        @keyframes slidein { from { opacity: 0; transform: translateX(-12px);} to { opacity: 1; transform: translateX(0);} }
      `}</style>
    </section>
  );
}
